// src/pages/Admin/Vendors/VendorDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Outlet, NavLink } from 'react-router-dom';
import {
    ArrowLeft,
    Store,
    Package,
    ShoppingBag,
    DollarSign,
    Settings,
    User,
    Mail,
    Phone,
    MapPin,
    Calendar,
    CheckCircle,
    XCircle,
    Clock,
    AlertCircle,
    Star,
    Users,
    Edit,
    MoreVertical
} from 'lucide-react';
import { adminAPI } from '../../../api/admin';
import showToast from '../../../utils/toast';
import { formatDateEnglish } from '../../../utils/date';
import { formatPhoneNumber } from '../../../utils/phoneFormatter';

interface VendorDetail {
    id: string;
    business_name: string;
    business_email: string;
    business_phone: string;
    business_address: any;
    business_type: string;
    website: string;
    description: string;
    verification_status: 'pending' | 'approved' | 'rejected';
    is_active: boolean;
    created_at: string;
    updated_at: string;
    verified_at: string | null;
    verified_by: any;
    rejection_reason: string | null;
    commission_rate: number;
    user: {
        id: string;
        email: string;
        first_name: string;
        last_name: string;
        phone: string;
    };
    stats?: {
        total_products: number;
        total_orders: number;
        total_revenue: number;
        rating: number;
        review_count: number;
    };
}

const tabs = [
    { name: 'Overview', to: '', icon: Store },
    { name: 'Products', to: 'products', icon: Package },
    { name: 'Orders', to: 'orders', icon: ShoppingBag },
    { name: 'Payouts', to: 'payouts', icon: DollarSign },
    { name: 'Settings', to: 'settings', icon: Settings },
];

const VendorDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [vendor, setVendor] = useState<VendorDetail | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchVendor();
    }, [id]);

    const fetchVendor = async () => {
        setLoading(true);
        try {
            const response = await adminAPI.getVendor(id!);
            setVendor(response.data);
        } catch (error) {
            showToast('Failed to load vendor details', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status: string, isActive: boolean) => {
        if (!isActive) {
            return { label: 'Inactive', className: 'bg-gray-100 text-gray-600', icon: XCircle };
        }
        const configs: Record<string, { label: string; className: string; icon: any }> = {
            pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
            approved: { label: 'Approved', className: 'bg-green-100 text-green-800', icon: CheckCircle },
            rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800', icon: XCircle },
        };
        return configs[status] || configs.pending;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    if (!vendor) {
        return (
            <div className="text-center py-12">
                <Store className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Vendor not found</p>
                <button
                    onClick={() => navigate('/dashboard/vendors')}
                    className="mt-4 text-blue-600 hover:text-blue-700"
                >
                    ← Back to Vendors
                </button>
            </div>
        );
    }

    const status = getStatusBadge(vendor.verification_status, vendor.is_active);
    const StatusIcon = status.icon;
    // Determine active tab based on current path
    const isActiveTab = (path: string) => {
        if (path === '' && location.pathname === `/dashboard/vendors/${id}`) return true;
        if (path !== '' && location.pathname.includes(`/dashboard/vendors/${id}/${path}`)) return true;
        return false;
    };

    return (
        <div className="space-y-6">
            {/* Vendor Header */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Store className="h-7 w-7 text-blue-600" />
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-xl font-bold text-gray-900">{vendor.business_name}</h1>
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.className}`}>
                                    <StatusIcon className="h-3 w-3" />
                                    {status.label}
                                </span>
                                {vendor.commission_rate && (
                                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                                        {vendor.commission_rate}% commission
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-gray-500">Vendor since {new Date(vendor.created_at).toLocaleDateString()}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigate(`/dashboard/vendors/${id}/settings`)}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium flex items-center gap-2"
                        >
                            <Edit className="h-4 w-4" />
                            Edit Vendor
                        </button>
                    </div>
                </div>

                {/* Quick Info */}
                <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span>{vendor.business_email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span>{formatPhoneNumber(vendor.business_phone) || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <User className="h-4 w-4 text-gray-400" />
                        <span>{vendor.user.first_name} {vendor.user.last_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span>Joined {formatDateEnglish(vendor.created_at)}</span>
                    </div>
                </div>

                {/* Stats */}
                {vendor.stats && (
                    <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="text-center">
                            <p className="text-xl font-bold text-gray-900">{vendor.stats.total_products || 0}</p>
                            <p className="text-xs text-gray-500">Products</p>
                        </div>
                        <div className="text-center">
                            <p className="text-xl font-bold text-gray-900">{vendor.stats.total_orders || 0}</p>
                            <p className="text-xs text-gray-500">Orders</p>
                        </div>
                        <div className="text-center">
                            <p className="text-xl font-bold text-gray-900">
                                ${(vendor.stats.total_revenue || 0).toFixed(0)}
                            </p>
                            <p className="text-xs text-gray-500">Revenue</p>
                        </div>
                        <div className="text-center">
                            <p className="text-xl font-bold text-gray-900">
                                {vendor.stats.rating ? vendor.stats.rating.toFixed(1) : 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500">Rating</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Tabs - Always visible */}
            <div className="border-b border-gray-200">
                <nav className="flex gap-1">
                    {tabs.map((tab) => {
                        const isActive = isActiveTab(tab.to);
                        return (
                            <NavLink
                                key={tab.name}
                                to={tab.to}
                                end={tab.to === ''}
                                className={({ isActive: active }) =>
                                    `flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors ${isActive || active
                                        ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                    }`
                                }
                            >
                                <tab.icon className="h-4 w-4" />
                                {tab.name}
                            </NavLink>
                        );
                    })}
                </nav>
            </div>

            {/* Tab Content */}
            <Outlet context={{ vendor, refresh: fetchVendor }} />
        </div>
    );
};

export default VendorDetail;